import { useRef, useCallback } from 'react';
import { triggerShakeAnimation, shakeInvalidFields } from '../../../../config/ui/forms/form-config';

export interface UseFormShakeReturn {
  formRef: React.RefObject<HTMLFormElement>;
  shakeForm: () => void;
  shakeField: (fieldName: string) => void;
  shakeInvalidFields: (errors: Record<string, string>) => void;
}

/**
 * Hook for managing form shake animations
 * Provides utilities to shake the entire form or individual fields
 */
export function useFormShake(): UseFormShakeReturn {
  const formRef = useRef<HTMLFormElement>(null);

  const shakeForm = useCallback(() => {
    triggerShakeAnimation(formRef.current, 'form');
  }, []);

  const shakeField = useCallback((fieldName: string) => {
    if (!formRef.current) return;
    
    const fieldElement = formRef.current.querySelector(`[name="${fieldName}"]`) as HTMLElement;
    if (fieldElement) {
      triggerShakeAnimation(fieldElement, 'input');
    }
  }, []);

  const shakeInvalidFieldsCallback = useCallback((errors: Record<string, string>) => {
    shakeInvalidFields(formRef.current, errors);
  }, []);

  return {
    formRef,
    shakeForm,
    shakeField,
    shakeInvalidFields: shakeInvalidFieldsCallback,
  };
}

/**
 * Hook for form submission with automatic shake animation
 * Automatically shakes invalid fields when form submission fails validation
 */
export function useFormSubmitWithShake(
  onValidSubmit: (formData: FormData, event: React.FormEvent<HTMLFormElement>) => void | Promise<void>,
  validateForm: () => { isValid: boolean; errors: Record<string, string> }
) {
  const { formRef, shakeInvalidFields } = useFormShake();

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    const validation = validateForm();
    
    if (!validation.isValid) {
      // Shake invalid fields
      shakeInvalidFields(validation.errors);
      return;
    }

    // Form is valid, proceed with submission
    const formData = new FormData(event.currentTarget);
    await onValidSubmit(formData, event);
  }, [onValidSubmit, validateForm, shakeInvalidFields]);

  return {
    formRef,
    handleSubmit,
    shakeInvalidFields,
  };
}